/**
 * Progress Bar Block - Interactivity API View Script
 *
 * Adds one class when the bar first scrolls into view. That is the whole job.
 *
 * WHY NOT DRIVE THE WIDTH FROM HERE
 * ---------------------------------
 * The obvious build collapses the bar on hydration and hands the width back
 * on intersection. It has a failure mode that is invisible in development:
 * browsers pause requestAnimationFrame — and with it the runtime's own
 * rendering — in a background tab, so a bar collapsed for the animation stays
 * empty. A page opened in a new tab, printed, or screenshotted then shows no
 * value at all, which is worse than showing no animation.
 *
 * So the width in the stylesheet is always the real value, and this file only
 * opts into a keyframe that starts from zero. Every way this script can fail —
 * module never loads, rendering paused, observer never fires — degrades to a
 * bar that is simply already full.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

store( 'brandy/progress-bar', {
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();

			// Filling up is decorative. Readers who opt out of motion, and
			// pages where the author turned the animation off, keep the bar
			// exactly as the server rendered it.
			if ( ! ctx.animate || prefersReducedMotion() ) {
				return;
			}

			const observer = new IntersectionObserver(
				( entries ) => {
					if ( ! entries[ 0 ].isIntersecting ) {
						return;
					}

					observer.disconnect();

					/*
					 * A hidden tab pauses animations but not the code that
					 * starts them, so a bar told to fill while the tab is in
					 * the background sits frozen on the keyframe's first
					 * frame — an empty bar, for as long as the reader stays
					 * away. Skipping the animation there costs a flourish
					 * nobody was watching and keeps the value correct.
					 */
					if ( document.visibilityState !== 'visible' ) {
						return;
					}

					ctx.filling = true;
				},
				{ threshold: 0.2 }
			);

			observer.observe( ref );

			return () => observer.disconnect();
		},
	},
} );
